import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Лента карточек с горизонтальной прокруткой — на всех экранах.
 *
 * Обратная связь человека из индустрии (2026-08-03): «весь сайт заделан
 * на скролле сверху вниз, это сбивает; отзывы и то, что вы делаете
 * сейчас, можно сделать в слайдер горизонтальный, а не листать всё так
 * же вертикально». До этого лента жила только на телефоне, а на мониторе
 * разворачивалась в сетку и снова тянула страницу вниз.
 *
 * Ширина карточки 84vw на телефоне и фикс на десктопе подобраны так,
 * чтобы следующая выглядывала краем: это единственный честный признак,
 * что полосу можно листать. Без «хвоста» полосу читают как одну карточку
 * и половину содержимого не видят никогда.
 *
 * Доступность: прокручиваемая область достижима с клавиатуры (WCAG 2.1.1)
 * — отсюда tabIndex и имя группы; стрелки дублируют жест мышью и гасятся
 * на краях, потому что стрелка, которая никуда не ведёт, врёт о наличии
 * контента.
 */
export function CardScroller({
  children,
  label,
  prevLabel,
  nextLabel,
  className = '',
}: {
  children: ReactNode
  /** Имя области для скринридера: «Кейсы до эпохи AI» и т.п. */
  label: string
  prevLabel: string
  nextLabel: string
  className?: string
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const sync = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setAtStart(el.scrollLeft <= 2)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2)
  }, [])

  useEffect(() => {
    sync()
    const el = trackRef.current
    if (!el) return
    el.addEventListener('scroll', sync, { passive: true })
    window.addEventListener('resize', sync)
    return () => {
      el.removeEventListener('scroll', sync)
      window.removeEventListener('resize', sync)
    }
  }, [sync])

  const step = (dir: -1 | 1) => {
    const el = trackRef.current
    if (!el) return
    const card = el.firstElementChild
    // Шаг — ровно одна карточка с зазором, иначе снап дёргает ленту назад.
    const delta = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.85
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollBy({ left: dir * delta, behavior: reduce ? 'auto' : 'smooth' })
  }

  const arrow = (dir: -1 | 1, disabled: boolean, aria: string) => (
    <button
      type="button"
      onClick={() => step(dir)}
      disabled={disabled}
      aria-label={aria}
      className="
        w-10 h-10 rounded-full border border-divider text-ink bg-bg
        flex items-center justify-center transition-opacity
        hover:border-ink disabled:opacity-30 disabled:hover:border-divider
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent
      "
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
        <polyline points={dir === -1 ? '9 2 4 7 9 12' : '5 2 10 7 5 12'} />
      </svg>
    </button>
  )

  return (
    <div className={className}>
      {/* Стрелки только там, где есть мышь: на телефоне листают пальцем,
          и две кнопки заняли бы место, которого и так мало. */}
      <div className="hidden sm:flex justify-end gap-3 mb-4">
        {arrow(-1, atStart, prevLabel)}
        {arrow(1, atEnd, nextLabel)}
      </div>
      <div
        ref={trackRef}
        role="group"
        aria-label={label}
        tabIndex={0}
        className="
          card-scroller flex gap-4 sm:gap-6 overflow-x-auto -mx-6 px-6 pb-5
          sm:mx-0 sm:px-0 snap-x snap-mandatory
          focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-card
        "
      >
        {children}
      </div>
    </div>
  )
}

/** Класс для прямого ребёнка CardScroller: держит ширину карточки в ленте
 *  и оставляет следующую выглядывающей краем. */
export const SCROLL_ITEM =
  'snap-start shrink-0 w-[84vw] max-w-[420px] sm:w-[360px] lg:w-[400px] xl:w-[420px]'
